import React from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./PageHeader";
import { Card } from "../ui/Card";
import { BookOpen, ArrowLeft } from "lucide-react";
import { cx } from "../../utils/functions";

export interface GuideSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    content: React.ReactNode;
}

interface GuideLayoutProps {
    pageTitle: string;
    backPath: string;
    backLabel: string;
    sidebarTitle: string;
    sections: GuideSection[];
    theme?: "emerald" | "indigo";
    cardVariant?: "primary" | "secondary";
    showSupportBox?: boolean;
}

const GuideLayout: React.FC<GuideLayoutProps> = ({
    pageTitle,
    backPath,
    backLabel,
    sidebarTitle,
    sections,
    theme = "emerald",
    cardVariant = "secondary",
    showSupportBox = false,
}) => {
    const isEmerald = theme === "emerald";

    const sidebarStyles = isEmerald
        ? "border-emerald-100 text-emerald-800"
        : "border-indigo-100 text-emerald-800";

    const navItemStyles = "hover:bg-emerald-50 hover:text-emerald-700 transition-colors";


    const sectionCardStyles = isEmerald
        ? "hover:border-emerald-100"
        : "hover:border-indigo-100";

    return (
        <div className="l0-layout">
            <PageHeader
                pageTitle={pageTitle}
                backButton={
                    <Link to={backPath} className="btn btn-neutral btn-sm flex items-center gap-2">
                        <ArrowLeft size={16} />
                        {backLabel}
                    </Link>
                }
            />

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sticky Sidebar Navigation */}
                <aside className="lg:col-span-1">
                    <div className="sticky top-24 space-y-4">
                        <div className={cx("bg-white rounded-2xl shadow-sm border p-4", sidebarStyles)}>
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <BookOpen size={20} />
                                {sidebarTitle}
                            </h3>
                            <nav className="flex flex-col space-y-1">
                                {sections.map((section) => (
                                    <a
                                        key={section.id}
                                        href={`#${section.id}`}
                                        className={cx(
                                            "px-4 py-2 text-sm font-medium rounded-xl transition-colors flex items-center gap-3 group",
                                            navItemStyles
                                        )}
                                    >
                                        <span className="group-hover:scale-110 transition-transform">
                                            {section.icon}
                                        </span>
                                        {section.title}
                                    </a>
                                ))}
                            </nav>
                        </div>

                        {showSupportBox && (
                            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
                                <p className="text-xs text-amber-800 font-medium">
                                    Need more help? Use the <strong>Report Bug</strong> button in the header to contact support.
                                </p>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Content Area */}
                <div className="lg:col-span-3 space-y-8 pb-12">
                    {sections.map((section) => (
                        <section
                            key={section.id}
                            id={section.id}
                            className="scroll-mt-24 transition-all duration-300"
                        >
                            <Card
                                variant={cardVariant}
                                className={cx(
                                    "overflow-hidden border-2 border-transparent transition-colors bg-white/60 backdrop-blur-sm",
                                    sectionCardStyles,
                                    !isEmerald && "shadow-xl"
                                )}
                            >
                                <div className="card-body p-6 sm:p-8">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100">
                                            {section.icon}
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-800">
                                            {section.title}
                                        </h2>
                                    </div>
                                    <div className="text-gray-600 leading-relaxed text-lg">
                                        {section.content}
                                    </div>
                                </div>
                            </Card>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GuideLayout;
